<template>
  <div class="landlords-eligibility-cover-content">
    <BaseAlert
      color="var(--blue-100)"
      class="mt-n4 pa-3 border-sm border-solid border-info"
      :class="$vuetify.breakpoint.lgAndUp ? 'mb-7' : 'mb-5'"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--blue-500)">mdi-information</v-icon>
        </div>
        <div
          class="black2-color"
          :class="$vuetify.breakpoint.lgAndUp ? 'mr-1' : ''"
        >
          <span class="font-weight-bold">Important!</span>
          Please confirm that you meet all of the below eligibility criteria.
          Take care to ensure that the information you provide is accurate to
          the best of your knowledge, otherwise your premium could increase,
          your claim may be refused or your policy could be voided
        </div>
      </div>
    </BaseAlert>

    <ul class="pa-0">
      <li v-if="showNewLetQuestion" class="d-flex mt-4">
        <span class="flex-shrink-0 white--text">1</span>
        <div class="flex-grow-1">
          <p class="font-16 mb-3">
            {{ NEW_LET_ELIGIBILITY_QUESTION.text }}
          </p>
          <div class="expand__wrapper d-flex align-start">
            <v-icon @click="newLetAccordionOpen = !newLetAccordionOpen">
              {{
                newLetAccordionOpen ? "mdi-chevron-down" : "mdi-chevron-right"
              }}
            </v-icon>
            <a
              class="action__item"
              @click="newLetAccordionOpen = !newLetAccordionOpen"
            >
              {{ NEW_LET_ELIGIBILITY_QUESTION.accordionLabel }}
            </a>
          </div>
          <div
            v-if="newLetAccordionOpen"
            class="expand-content__wrapper"
            v-html="NEW_LET_ELIGIBILITY_QUESTION.expandContent"
          ></div>

          <BaseToggleButton
            :value="newLetAnswer"
            :toggleButtonText="{
              yesDataUiid: 'NewLetOrProductTransfer_Yes',
              noDataUiid: 'NewLetOrProductTransfer_No',
            }"
            height="48"
            :minWidth="'100'"
            class="mt-3 mb-4"
            @change="setNewLetAnswer"
          />

          <template v-if="newLetAnswer === false">
            <BaseAlert
              color="var(--orange-100)"
              class="pa-4 border-sm border-solid border-warning"
            >
              <div class="d-flex flex-no-wrap">
                <div class="mr-3">
                  <v-icon color="var(--orange-500)">mdi-alert</v-icon>
                </div>
                <div>
                  <span class="font-weight-bold">Important!</span>
                  {{ NEW_LET_ELIGIBILITY_QUESTION.warningText }}
                </div>
              </div>
            </BaseAlert>

            <BaseCheckBox
              :checked="newLetCheckboxTicked"
              :label="NEW_LET_ELIGIBILITY_QUESTION.checkboxLabel"
              className="mt-0 new-let-checkbox font-weight-bold"
              @change="onNewLetCheckboxChange"
            />
          </template>
        </div>
      </li>

      <li
        class="d-flex mt-4"
        v-for="(eligibility, index) in LANDLORD_ELIGIBILITY_COVER"
        :key="index"
      >
        <span class="flex-shrink-0 white--text">{{
          showNewLetQuestion ? parseInt(eligibility?.id) + 1 : eligibility?.id
        }}</span>
        <div>
          <p class="font-16 mb-3">
            {{ eligibility?.text }}
          </p>
          <div class="expand__wrapper d-flex align-start">
            <v-icon
              @click="
                indexOfContentExpand === index
                  ? closeExpandContent()
                  : expandContent(index)
              "
            >
              {{
                indexOfContentExpand === index
                  ? eligibility?.iconClose
                  : eligibility?.iconOpen
              }}
            </v-icon>
            <a
              class="action__item"
              v-if="eligibility?.expandContent"
              @click="
                indexOfContentExpand === index
                  ? closeExpandContent()
                  : expandContent(index)
              "
              >{{ eligibility?.eventTextDisplay }}
            </a>
          </div>
          <div
            v-if="indexOfContentExpand === index"
            class="expand-content__wrapper"
            v-html="eligibility?.expandContent"
          ></div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import {
  LANDLORD_ELIGIBILITY_COVER,
  NEW_LET_ELIGIBILITY_QUESTION,
} from "@/constants/eligibility.constants";
import BaseAlert from "@/components/BaseComponents/BaseAlert";
import BaseCheckBox from "@/components/BaseComponents/BaseCheckBox";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";

export default {
  name: "LandlordsEligibilityCover",
  components: {
    BaseAlert,
    BaseCheckBox,
    BaseToggleButton,
  },
  props: {
    showNewLetQuestion: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      indexOfContentExpand: -1,
      LANDLORD_ELIGIBILITY_COVER,
      NEW_LET_ELIGIBILITY_QUESTION,
      newLetAnswer: null,
      newLetAccordionOpen: false,
      newLetCheckboxTicked: false,
    };
  },
  watch: {
    newLetAnswer() {
      this.newLetCheckboxTicked = false;
      this.emitNewLetState();
    },
  },
  methods: {
    expandContent(index) {
      this.indexOfContentExpand = index;
    },
    closeExpandContent() {
      this.indexOfContentExpand = -1;
    },
    setNewLetAnswer(value) {
      this.newLetAnswer = value;
    },
    onNewLetCheckboxChange(value) {
      this.newLetCheckboxTicked = value;
      this.emitNewLetState();
    },
    emitNewLetState() {
      this.$emit("newLetStateChanged", {
        answered: this.newLetAnswer !== null,
        value: this.newLetAnswer,
        checkboxTicked: this.newLetCheckboxTicked,
      });
    },
    resetNewLetState() {
      this.newLetAnswer = null;
      this.newLetAccordionOpen = false;
      this.newLetCheckboxTicked = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.landlords-eligibility-cover-content {
  ul {
    list-style: none;
    margin: 0;
    padding: 0 0 0 4px;

    li {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }

      > span {
        background: $stepper-active;
        border-radius: 50%;
        font-size: 18px;
        font-weight: 800;
        height: 28px;
        margin-right: 20px;
        width: 28px;

        display: flex;
        justify-content: center;
        align-items: center;
      }
      p {
        margin: 0;
      }

      .expand__wrapper {
        margin-left: -6px;
        .action__item {
          color: #106175;
          font-size: 16px;
          font-weight: 600;
          text-decoration: underline;
        }
        .v-icon {
          color: #106175;
        }
      }
    }
  }

  .expand-content__wrapper {
    padding: 8px 0px 8px 16px;
    margin: 12px 0px 0px 6px;
    border-left: 2px solid #106175;
    font-size: 16px;
  }

  .new-let-checkbox {
    :deep(.v-label) {
      color: currentColor !important;
    }
  }
}
</style>
